import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Sekmeler,
  cn,
  type IkonAdi,
} from "@koclukpanel/ui";
import { useSudoKapisi, useSudoOnce } from "./SudoKapisi";
import {
  ApiHatasi,
  izinKatalogu,
  kullaniciIzinleri,
  kullaniciIzniVer,
  personelListesi,
  rolAta,
  rolGetir,
  rolIzinleriGuncelle,
  rolOlustur,
  rolDurumuGuncelle,
  rolYuzeyiGuncelle,
  type RolYuzeyi,
  rolleriGetir,
  sudoDurum,
  type Kapsam,
  type KatalogIzni,
  type RolOzeti,
} from "./api";

/**
 * ROL VE IZIN YONETIMI — F-ROL
 *
 * ================================================================
 * EKRAN KARAR VERMIYOR
 * ================================================================
 * Hangi iznin tiklenebilecegi (`verebilirMiyim`), hangi kapsamin
 * secilebilecegi (`kendiKapsamim`) ve hangi rolun duzenlenebilecegi
 * (`duzenleyebilirMiyim`) SUNUCUDAN geliyor.
 *
 * Ekran hesaplasaydi yetki motorunun kurallari burada TEKRAR yazilmis
 * olurdu ve ikisi ayrildigi gun ekran tiklenebilir gosterip sunucu
 * reddederdi — kullanici da "sistem bozuk" derdi.
 *
 * Ekranin isi: sunucunun soyledigini gostermek ve NEDEN oyle
 * oldugunu yazmak.
 */

const KAPSAM_ADI: Record<Kapsam, string> = {
  ALL: "Tümü",
  TEAM: "Ekip",
  ASSIGNED: "Atanan",
  SELF: "Kendi",
  LINKED: "Bağlı",
  LEAD_ONLY: "Yalnız aday",
  NONE: "Yok",
};

const RISK_ADI: Record<number, string> = { 1: "Düşük", 2: "Orta", 3: "Yüksek" };
const RISK_SINIFI: Record<number, string> = {
  1: "bg-notr-100 text-notr-600",
  2: "bg-uyari-500/15 text-uyari-800",
  3: "bg-hata-500/15 text-hata-700",
};

/**
 * YETKİ PENCERESİ ÇİPİ — `RolIzinler.dc.html`
 *
 * ================================================================
 * SAYAÇ UYDURULMUYOR, SUNUCUDAN GELİYOR
 * ================================================================
 * `GET /auth/sudo/durum` `{ acik, kalanSaniye }` döndürüyor. Kalan
 * süreyi "şifreyi girdiğim andan itibaren 15 dakika" diye istemcide
 * hesaplamak mümkündü ve YANLIŞ olurdu: pencere sunucuda uzayabilir,
 * kapatılabilir (`sudoKapat`) ya da oturum başka bir sekmede
 * yenilenebilir. Ekranda duran sayı o zaman kimsenin doğrulamadığı
 * bir tahmin olurdu.
 *
 * Saniye başı azalma yereldir ama BAŞLANGICI sunucunundur ve her
 * dakika yeniden soruluyor — sapma birikmiyor.
 *
 * ================================================================
 * KAPALIYKEN ÇİP HİÇ ÇİZİLMİYOR
 * ================================================================
 * "Yetki penceresi kapalı" yazmak bir uyarı gibi okunurdu; oysa kapalı
 * olması NORMAL hâl. Sorgu düşerse de çizilmiyor: pencerenin açık olup
 * olmadığını bilmiyoruz ve ikisinden birini yazmak iddia olurdu.
 */
function YetkiPenceresi() {
  const durum = useQuery({
    queryKey: ["sudo-durum"],
    queryFn: sudoDurum,
    retry: false,
    // Sunucuyla her dakika yeniden hizalaniyor.
    refetchInterval: 60_000,
  });

  const [kalan, setKalan] = useState<number | null>(null);

  useEffect(() => {
    if (!durum.data?.acik) {
      setKalan(null);
      return;
    }
    setKalan(durum.data.kalanSaniye);
    const s = setInterval(() => setKalan((k) => (k === null ? null : Math.max(0, k - 1))), 1000);
    return () => clearInterval(s);
  }, [durum.data?.acik, durum.data?.kalanSaniye]);

  if (kalan === null || kalan <= 0) return null;

  const dk = Math.floor(kalan / 60);
  const sn = kalan % 60;

  return (
    <span className="rounded-alan bg-uyari-50 ring-uyari-200 inline-flex items-center gap-2 px-3 py-2 ring-1 ring-inset">
      <Ikon ad="bekliyor" boyut="kucuk" className="text-uyari-800" />
      <span className="text-uyari-800 text-[13px]">
        Yetki penceresi açık ·{" "}
        {/*
          `tabular-nums` (`:root`) sayesinde rakamlar sabit genislikte:
          sayac gerilerken cip surekli genisleyip daralmiyor.
        */}
        <strong>
          {dk}:{String(sn).padStart(2, "0")}
        </strong>
      </span>
    </span>
  );
}

/**
 * Künye satırı — "9 sistem rolü · 109 izin".
 *
 * Her parça KENDİ kaynağından geliyor ve gelmeyeni YAZILMIYOR: rol
 * sorgusu düşerse rol sayısı, katalog düşerse izin sayısı hiç
 * çizilmiyor. Sıfır yazmak "hiç rol yok" demek olurdu
 * (`docs/03-kod-standartlari.md` — "ekranın söyleyemediğini uydurma").
 */
function Kunye() {
  // Ikisi de sudo istiyor (`sistem.rol.ata`): pencere kapaliyken 403 atmasinlar (UI/UX Dalga 2, M6).
  // Sifre kapisini liste ciziyor; burada yalnizca "hazir mi" bilgisi kullaniliyor.
  const { sudoHazir } = useSudoOnce();
  const roller = useQuery({
    queryKey: ["roller"],
    queryFn: rolleriGetir,
    retry: false,
    enabled: sudoHazir,
  });
  const katalog = useQuery({
    queryKey: ["izin-katalogu"],
    queryFn: izinKatalogu,
    retry: false,
    enabled: sudoHazir,
  });

  const parcalar: string[] = [];
  if (roller.data) {
    const sistem = roller.data.filter((r) => r.isSystem).length;
    const ozel = roller.data.length - sistem;
    parcalar.push(`${sistem} sistem rolü`);
    // Ozel rol YOKSA "0 özel rol" yazilmiyor: sifir bir bilgi degil,
    // gurultu — sistem bos aciliyor ve ozel rol istisna.
    if (ozel > 0) parcalar.push(`${ozel} özel rol`);
  }
  if (katalog.data) parcalar.push(`${katalog.data.length} izin`);

  if (parcalar.length === 0) return null;
  return <p className="text-notr-600 text-sm">{parcalar.join(" · ")}</p>;
}

/** Çizimin alt şeridindeki üç kural kartı. */
function KuralKarti({ ikon, children }: { ikon: IkonAdi; children: ReactNode }) {
  return (
    <div className="rounded-kart border-notr-200 bg-notr-0 flex items-start gap-2.5 border px-4 py-3.5">
      <Ikon ad={ikon} boyut="kucuk" className="text-notr-600 mt-0.5 shrink-0" />
      <p className="text-notr-600 text-[13px] leading-[1.5]">{children}</p>
    </div>
  );
}

export function Roller() {
  const [gorunum, setGorunum] = useState<"roller" | "yeni" | "kullanici">("roller");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Kunye />
        <YetkiPenceresi />
      </div>

      {/*
        SEKME CIPI `Sekmeler`E GECTI. Onceden burada elle yazilmis bir
        `role="tab"` seridi vardi ve ARIA sekme deseninin yalnizca
        YARISINI kuruyordu: ok tuslariyla gezinme, gezinen odak ve
        `tabpanel` bagi yoktu. Ekran okuyucu kullanicisina "burada ok
        tuslariyla gezebilirsin" diye soz verip tutmamak, hic soz
        vermemekten kotu.
      */}
      <Sekmeler
        etiket="Rol yönetimi bölümleri"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "roller", etiket: "Roller" },
          { deger: "yeni", etiket: "Yeni özel rol" },
          { deger: "kullanici", etiket: "Kullanıcı bazlı izin" },
        ]}
      />

      {gorunum === "roller" && <RolListesi />}
      {gorunum === "yeni" && <YeniRol />}
      {gorunum === "kullanici" && <KullaniciIzni />}

      {/*
        UC KURAL KARTI — cizimin alt seridi. Onceden hepsi tek bir sari
        paragraftaydi; cizim onlari ayirdi cunku UC AYRI kural ve
        ucu de ayri bir soruyu cevapliyor. Tek paragrafta okunmuyorlardi.

        Kartlar EKRANIN ALTINDA duruyor, ustunde degil: kullanici buraya
        is yapmaya geliyor ve kurallari isten once okumasi beklenmiyor —
        ama takildiginda hemen altinda bulmasi gerekiyor.
      */}
      <div className="grid gap-3 md:grid-cols-3">
        <KuralKarti ikon="kilit">
          <strong className="text-notr-800">Sahip olmadığınız izni veremezsiniz.</strong> Kutusu
          olmayan satırlar bunlar — gizlemiyoruz, çünkü “böyle bir izin yok” izlenimi yanlış olurdu.
        </KuralKarti>
        <KuralKarti ikon="bilgi">
          <strong className="text-notr-800">Kendi izninizi değiştiremezsiniz.</strong> Kendi
          rolünüzü seçtiğinizde bu panel salt okunur açılır{/* F-ROL-16 */}.
        </KuralKarti>
        <KuralKarti ikon="rol">
          İzin kodu üç bölümlü: <code className="text-xs">modül.kaynak.işlem</code>. Arama bu yapıya
          göre süzüyor — izinler listelenerek okunmuyor.
        </KuralKarti>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

/**
 * ROL SEÇİMİ İKİ SÜTUN — `RolIzinler.dc.html`
 *
 * ================================================================
 * AKORDEONDAN USTA-AYRINTI DÜZENİNE
 * ================================================================
 * Önceden her rol bir karttı ve "İzinleri gör" ile açılıyordu. Dokuz
 * sistem rolü varken bu şu demekti: bir rolün izinlerine bakarken
 * ötekilerin adları ekrandan kayıyor ve "COACH'ta 61, ACADEMIC_LEAD'de
 * 53 izin var" karşılaştırması yapılamıyordu.
 *
 * Çizim rolleri SABİT bir sütunda tutuyor ve yanlarına izin sayılarını
 * yazıyor. Karşılaştırma bu ekranın asıl işi: "bu role fazla mı izin
 * vermişiz" sorusu ancak yan yana görülünce sorulabiliyor.
 *
 * Dar ekranda sütun üste geçiyor; 250px'lik bir şeridi telefona
 * sıkıştırmak listeyi de paneli de okunmaz yapardı.
 */
function RolListesi() {
  const [secili, setSecili] = useState<string | null>(null);
  const { sorguKapisi } = useSudoKapisi(
    "Rol listesi hassas; görüntülemek için kimliğinizi doğrulayın.",
  );
  // Pencere kapaliysa sifre ONCE sorulur, liste sorgusu 403 atmaz (UI/UX Dalga 2, M6).
  const { sudoHazir, onKapi } = useSudoOnce(
    "Rol listesi hassas; görüntülemek için kimliğinizi doğrulayın.",
  );

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["roller"],
    queryFn: rolleriGetir,
    // 403 tekrar denemekle gecmez; sudo penceresi acilmadan hep ayni
    // cevap gelir ve varsayilan yeniden denemeler yalnizca bekletir.
    retry: false,
    enabled: sudoHazir,
  });

  if (onKapi) return <>{onKapi}</>;
  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    /*
      LISTENIN KENDISI sudo istiyor (`sistem.rol.ata`). Onceden burada
      "Tekrar dene" dugmesi vardi ve o dugme sonsuza kadar ayni 403'u
      alirdi: kullaniciya yapacak bir sey verilmis GIBI gorunuyor, ama
      verilmiyordu.
    */
    const kapiEkrani = sorguKapisi(error, () => void refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;

    return (
      <BosDurum
        ton="hata"
        baslik="Roller alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.length === 0) {
    return <BosDurum baslik="Rol yok" aciklama="Sistem rolleri kurulum sırasında yazılır." />;
  }

  /*
    ILK ROL KENDILIGINDEN SECILI. Bos bir panelle acilsaydi ekran
    "once soldan birine bas" diyen bir bosluk gosterirdi; cizim de
    acilista bir rolu secili cizyor.
  */
  const acik = data.find((r) => r.id === secili) ?? data[0]!;

  return (
    <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
      <nav aria-label="Roller" className="flex w-full shrink-0 flex-col gap-[7px] lg:w-[250px]">
        <span className="text-notr-600 px-0.5 text-xs font-semibold tracking-[0.03em] uppercase">
          Roller
        </span>
        {data.map((r) => (
          <RolDugmesi key={r.id} rol={r} secili={r.id === acik.id} onSec={() => setSecili(r.id)} />
        ))}
      </nav>

      <div className="min-w-0 flex-1">
        <RolPaneli rol={acik} />
      </div>
    </div>
  );
}

function RolDugmesi({ rol, secili, onSec }: { rol: RolOzeti; secili: boolean; onSec: () => void }) {
  return (
    <button
      type="button"
      onClick={onSec}
      /*
        `aria-current="true"`: ekran okuyucu hangi rolun acik oldugunu
        soyluyor. `aria-pressed` yanlis olurdu — bunlar bir acik/kapali
        anahtari degil, bir liste icinde KONUM.
      */
      aria-current={secili ? "true" : undefined}
      className={cn(
        "rounded-alan flex items-center justify-between gap-2 border px-3 py-2.5 text-left",
        "ease-cikis transition-[border-color,box-shadow] duration-(--sure-anlik)",
        secili
          ? "border-marka-600 bg-notr-0 shadow-yukselen"
          : "border-notr-200 bg-notr-0 hover:border-notr-300",
      )}
    >
      <span
        className={cn(
          "text-sm",
          secili ? "text-notr-900 font-semibold" : "text-notr-800",
          !rol.isActive && "text-notr-600",
        )}
      >
        {rol.name}
        {!rol.isActive && <span className="text-notr-600 font-normal"> · pasif</span>}
      </span>
      {/*
        IZIN SAYISI BU SUTUNUN ASIL ISI: dokuz rolun sayilari yan yana
        gorununce "bu role fazla mi vermisiz" sorusu sorulabiliyor.
        Tek tek acilir kartlarda o karsilastirma hic yapilamiyordu.
      */}
      <span
        className={cn(
          "text-xs tabular-nums",
          secili ? "text-marka-700 font-semibold" : "text-notr-600",
        )}
      >
        {rol.izinSayisi}
      </span>
    </button>
  );
}

/** Yüzeyin ekranda okunan karşılığı. */
const YUZEY_ADI: Record<RolYuzeyi, string> = {
  PANEL: "Yönetim panelinde",
  STUDENT_APP: "Öğrenci ve veli uygulamasında",
};

/**
 * YÜZEY DÜZELTME — F-ROL-04b
 *
 * ================================================================
 * NEDEN DÜZELTİLEBİLİR OLMAK ZORUNDA
 * ================================================================
 * Yüzey rol oluşturulurken soruluyor. Yalnızca orada sorulsaydı
 * yanlış seçim düzeltilemezdi: rolü taşıyan herkes kullanamadığı bir
 * uygulamada kalır ve tek çıkış yolu rolü arşivleyip yenisini kurmak
 * olurdu — atamalar da onunla birlikte giderdi.
 *
 * Göçün kendisi de bunu gerektiriyor: var olan özel roller geri
 * doldurulurken PANEL aldı (bugünkü davranış oydu). O tahminlerden
 * yanlış olanlar buradan düzeltiliyor.
 */
function RolYuzeyi({ rol }: { rol: RolOzeti }) {
  const istemci = useQueryClient();
  const { hataYakala, kapi } = useSudoKapisi(
    "Rol ve izin değişiklikleri geri alınması zor sonuçlar doğurur. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const mut = useMutation({
    mutationFn: (yuzey: RolYuzeyi) => rolYuzeyiGuncelle(rol.id, yuzey),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["roller"] });
    },
    onError: hataYakala(() => mut.mutate(hedef)),
  });

  const hedef: RolYuzeyi = rol.surface === "PANEL" ? "STUDENT_APP" : "PANEL";

  return (
    <div className="border-notr-200 flex flex-col gap-2 border-t pt-3">
      {kapi}
      {/*
        MEVCUT YÜZEY BURADA TEKRAR YAZILMIYOR: üst özet satırı zaten
        "… açılıyor" diyor. Ölçüldü (07.09.2026, tarayıcıda) — iki
        cümle yan yana aynı şeyi söylüyordu ve okuyan, ikisi arasında
        bir fark aramak zorunda kalıyordu.

        Kalan metin DEĞİŞİKLİĞİN NE ANLAMA GELDİĞİNİ söylüyor; düğme
        tek başına "kaç kişiyi etkiliyor" sorusunu cevaplamıyor.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-notr-600 min-w-0 flex-1 text-xs">
          {rol.kullaniciSayisi > 0
            ? `Bu rolü ${rol.kullaniciSayisi} kullanıcı taşıyor.`
            : "Bu rolü henüz kimse taşımıyor."}
        </p>
        <Buton gorunum="ikincil" disabled={mut.isPending} onClick={() => mut.mutate(hedef)}>
          {mut.isPending ? "Değiştiriliyor…" : `${YUZEY_ADI[hedef]} açılsın`}
        </Buton>
      </div>
      {/*
        DEĞİŞİKLİĞİN SONUCU SÖYLENİYOR. "Yüzey değişti" tek başına
        yetmez: rolü taşıyan kişiler o an başka bir uygulamadalar ve
        bir sonraki isteklerinde yer değiştirecekler.
      */}
      {mut.isSuccess && (
        <p role="status" className="text-onay-700 text-xs">
          Değişti. Bu rolü taşıyan {rol.kullaniciSayisi} kullanıcı bir sonraki isteğinde yeni
          uygulamaya yönlendirilecek — yeniden giriş gerekmiyor.
        </p>
      )}
      {mut.isError && (
        <p role="alert" className="text-hata-500 text-xs">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Yüzey değiştirilemedi."}
        </p>
      )}
    </div>
  );
}

function RolPaneli({ rol }: { rol: RolOzeti }) {
  return (
    <article className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3.5 border p-5">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-notr-900 text-sm font-semibold">
            {rol.name}{" "}
            <span className="text-notr-600 font-mono text-xs font-normal">{rol.code}</span>
          </h3>
          {rol.description && <p className="text-notr-600 mt-1 text-sm">{rol.description}</p>}
          <p className="text-notr-600 mt-2 text-xs">
            {rol.izinSayisi} izin · {rol.kullaniciSayisi} kullanıcı · varsayılan kapsam{" "}
            {KAPSAM_ADI[rol.defaultScope]}
          </p>
          {/*
            YÜZEY GÖRÜNÜR YAZILIYOR — F-ROL-04b.

            Yanlış seçilmiş bir yüzeyi fark etmenin yolu önce onu
            GÖRMEK. Yalnızca oluşturma formunda sorulup sonra hiçbir
            yerde yazılmasaydı, hata ancak kullanıcı "giremiyorum"
            dediğinde ortaya çıkardı.
          */}
          <p className="text-notr-600 mt-1 text-xs">{YUZEY_ADI[rol.surface]} açılıyor</p>
        </div>

        {rol.isSystem && (
          <Rozet ton="bilgi" className="shrink-0">
            Sistem rolü
          </Rozet>
        )}
        {!rol.isActive && (
          <Rozet ton="uyari" className="shrink-0">
            Pasif
          </Rozet>
        )}
      </div>

      {/*
        KENDI ROLUN oldugunda SEBEP yaziliyor, panel sessizce salt
        okunur acilmiyor. Sessiz olsaydi yonetici "neden
        duzenleyemiyorum" diye arardi; oysa kural bilincli ve
        soylenmeye deger.
      */}
      {!rol.duzenleyebilirMiyim && (
        <p className="text-uyari-800 text-xs">
          Bu rolü siz taşıyorsunuz; kendi rolünüzü düzenleyemezsiniz{/* F-ROL-16 */}. Değişiklik
          gerekiyorsa başka bir yetkiliye yaptırın.
        </p>
      )}

      {/*
        `key`: rol degisince izin paneli SIFIRDAN kuruluyor. Olmasaydi
        onceki rolde yapilmis kaydedilmemis secim yeni rolde duruyor
        gorunurdu ve "kaydet" yanlis role yazardi.
      */}
      {/*
        YÜZEY DÜZELTME YALNIZCA ÖZEL ROLDE. Sistem rolünün yüzeyi bir
        ÜRÜN kararı — `STUDENT`i panele taşımak öğrenciye personel
        kabuğunu açmak olurdu. Sunucu da 409 döndürüyor; buradaki
        gizleme bir kolaylık, koruma değil.
      */}
      {!rol.isSystem && rol.duzenleyebilirMiyim && <RolYuzeyi key={`y-${rol.id}`} rol={rol} />}

      {rol.duzenleyebilirMiyim && <RolDurumu key={`d-${rol.id}`} rol={rol} />}

      <RolIzinleri key={rol.id} rolId={rol.id} duzenlenebilir={rol.duzenleyebilirMiyim} />
    </article>
  );
}

/**
 * ROLÜ PASİFLEŞTİRME — F-ROL-05b (05.10.2026, müşteri revizesi madde 4)
 *
 * Firma şimdilik yalnızca yönetici ve eğitim koçuyla çalışıyor; diğer
 * roller atama listesinden kalksın ama silinmesin. Atanmış kullanıcısı
 * olan rol kapanmıyor: kapansaydı o kişilerin yetkisi sessizce düşerdi.
 */
function RolDurumu({ rol }: { rol: RolOzeti }) {
  const istemci = useQueryClient();
  const { hataYakala, kapi } = useSudoKapisi(
    "Rol değişiklikleri kimin neyi göreceğini etkiler. Devam etmeden önce şifrenizi doğrulayın.",
  );
  const hedef = !rol.isActive;
  /*
    PASİFLEŞTİRME ÖZETLİ ONAY İSTİYOR — UI/UX Dalga 1 (O11). Yetki
    değiştiren işlemler arasında tek tıkla olan buydu; rol atama
    listesinden kalkıyor ve geri açmak başka birinin dikkatini istiyor.
    Yeniden ETKİNLEŞTİRME sorulmuyor: kimsenin yetkisini değiştirmiyor,
    yalnız rolü atanabilir yapıyor.
  */
  const [onay, setOnay] = useState(false);
  const mut = useMutation({
    mutationFn: () => rolDurumuGuncelle(rol.id, hedef),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["roller"] });
    },
    onError: hataYakala(() => mut.mutate()),
  });

  /*
    KARAR SUNUCUDAN — Codex tur 36 B7. Ekran önce temel rol listesini ve
    `kullaniciSayisi`nı (süresi dolmuşlar DAHİL) kendisi yorumluyordu;
    yalnızca süresi dolmuş ataması olan rolde sunucu izin verirken düğme
    kapalı kalıyordu. `pasifKarari` kapının kullandığı fonksiyonun çıktısı.
  */
  const karar = rol.pasifKarari;
  if (rol.isActive && !karar.olur && karar.neden === "TEMEL_ROL") {
    return (
      <p className="border-notr-200 text-notr-600 border-t pt-3 text-xs">
        Bu rol sistemin çalışması için gerekli; pasifleştirilemez.
      </p>
    );
  }

  const ataliVar = rol.isActive && !karar.olur && karar.neden === "ATAMA_VAR";
  return (
    <div className="border-notr-200 flex flex-col gap-2 border-t pt-3">
      {kapi}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-notr-600 min-w-0 flex-1 text-xs">
          {rol.isActive
            ? ataliVar
              ? `Bu rolü ${rol.etkinKullaniciSayisi} kullanıcı taşıyor; pasifleştirmeden önce rolü kişilerden alın.`
              : "Kullanılmıyorsa pasifleştirin: atama listesinden kalkar, silinmez, istenince geri açılır."
            : "Pasif: atama listesinde görünmüyor. İzinleri ve geçmişi duruyor."}
        </p>
        <Buton
          gorunum="ikincil"
          disabled={mut.isPending || ataliVar || onay}
          aria-expanded={rol.isActive ? onay : undefined}
          onClick={() => (rol.isActive ? setOnay(true) : mut.mutate())}
        >
          {mut.isPending ? "Değiştiriliyor…" : rol.isActive ? "Pasifleştir" : "Yeniden etkinleştir"}
        </Buton>
      </div>
      {onay && rol.isActive && (
        <div
          role="alertdialog"
          aria-label="Pasifleştirme onayı"
          onKeyDown={(e) => {
            if (e.key === "Escape") setOnay(false);
          }}
          className="rounded-alan border-uyari-200 bg-uyari-50 flex flex-col gap-3 border p-3"
        >
          <p className="text-notr-800 text-sm">
            <strong>{rol.name}</strong> atama listesinden kalkacak; kimseye yeniden verilemez.
            İzinleri ve geçmişi silinmez, istenince geri açılır.
          </p>
          <div className="flex flex-wrap gap-2">
            <Buton
              yukleniyor={mut.isPending}
              onClick={() => {
                setOnay(false);
                mut.mutate();
              }}
            >
              Evet, pasifleştir
            </Buton>
            <Buton gorunum="sessiz" onClick={() => setOnay(false)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
      {mut.isError && (
        <p role="alert" className="text-hata-500 text-xs">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Rol durumu değiştirilemedi."}
        </p>
      )}
    </div>
  );
}

function RolIzinleri({ rolId, duzenlenebilir }: { rolId: string; duzenlenebilir: boolean }) {
  const istemci = useQueryClient();
  const [secim, setSecim] = useState<Map<string, Kapsam> | null>(null);

  const rol = useQuery({ queryKey: ["rol", rolId], queryFn: () => rolGetir(rolId) });
  const katalog = useQuery({ queryKey: ["izin-katalogu"], queryFn: izinKatalogu });

  const { hataYakala, kapi } = useSudoKapisi(
    "Rol ve izin değişiklikleri geri alınması zor sonuçlar doğurur. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const mut = useMutation({
    mutationFn: () =>
      rolIzinleriGuncelle(
        rolId,
        [...(secim ?? new Map())].map(([code, scope]) => ({ code, scope })),
      ),
    onSuccess: async () => {
      setSecim(null);
      await istemci.invalidateQueries({ queryKey: ["rol", rolId] });
      await istemci.invalidateQueries({ queryKey: ["roller"] });
    },
    onError: hataYakala(() => mut.mutate()),
  });

  if (rol.isPending || katalog.isPending) {
    return <Iskelet satir={4} />;
  }
  if (rol.isError || katalog.isError) {
    return (
      <p role="alert" className="text-hata-500 mt-3 text-sm">
        İzin listesi alınamadı.
      </p>
    );
  }

  const mevcut = secim ?? new Map(rol.data.permissions.map((p) => [p.code, p.scope]));

  return (
    <div className="border-notr-200 mt-3 border-t pt-3">
      {kapi}
      <IzinSecici
        katalog={katalog.data}
        secili={mevcut}
        salt={!duzenlenebilir}
        onDegis={(m) => setSecim(new Map(m))}
      />

      {duzenlenebilir && secim !== null && (
        <div className="mt-3 flex gap-2">
          <Buton onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Kaydediliyor…" : "Değişiklikleri kaydet"}
          </Buton>
          <Buton gorunum="sessiz" onClick={() => setSecim(null)}>
            Vazgeç
          </Buton>
        </div>
      )}

      {mut.isError && (
        <p role="alert" className="text-hata-500 mt-2 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Kaydedilemedi."}
        </p>
      )}
      {mut.isSuccess && (
        <p role="status" className="text-onay-700 mt-2 text-sm">
          {mut.data.degisenSayisi} izin değişti.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

/**
 * Izin secici — modul modul gruplanmis tikleme listesi.
 *
 * ================================================================
 * VERILEMEYEN IZIN KAPALI VE SEBEBI YAZILI
 * ================================================================
 * Iki ayri sebep var ve ikisi FARKLI sey soyluyor:
 *   kisitli izin      -> ozel role HIC eklenemez (sistem rolune ozel)
 *   sende yok         -> once sana verilmeli
 *
 * Ikisini ayni "kapali" gorunumuyle gostermek, yoneticiyi yanlis
 * yere gonderirdi: birincisinde yapacak bir sey yok, ikincisinde
 * cozum belli.
 */
function IzinSecici({
  katalog,
  secili,
  salt,
  onDegis,
}: {
  katalog: KatalogIzni[];
  secili: Map<string, Kapsam>;
  salt: boolean;
  onDegis: (m: Map<string, Kapsam>) => void;
}) {
  const [arama, setArama] = useState("");

  const modullere = useMemo(() => {
    const q = arama.trim().toLowerCase();
    const suzulmus = q
      ? katalog.filter((p) => p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q))
      : katalog;

    const m = new Map<string, KatalogIzni[]>();
    for (const p of suzulmus) m.set(p.module, [...(m.get(p.module) ?? []), p]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, "tr"));
  }, [katalog, arama]);

  const degistir = (kod: string, kapsam: Kapsam | null) => {
    const yeni = new Map(secili);
    if (kapsam === null) yeni.delete(kod);
    else yeni.set(kod, kapsam);
    onDegis(yeni);
  };

  return (
    <div className="flex flex-col gap-3">
      <Alan
        etiket="İzin ara"
        value={arama}
        onChange={(e) => setArama(e.target.value)}
        placeholder="kisi.ogrenci"
      />

      <p className="text-notr-600 text-xs">
        {secili.size} izin seçili · {katalog.length} izin tanımlı
      </p>

      {modullere.length === 0 ? (
        <p className="text-notr-600 text-sm">Aramayla eşleşen izin yok.</p>
      ) : (
        modullere.map(([modul, izinler]) => (
          <fieldset key={modul} className="flex flex-col gap-1">
            <legend className="text-notr-600 text-xs font-semibold tracking-wide uppercase">
              {modul}
            </legend>
            <ul className="flex flex-col gap-1">
              {izinler.map((p) => {
                const secilenKapsam = secili.get(p.code) ?? null;
                const kapali = salt || !p.verebilirMiyim;
                /*
                  ================================================================
                  VERİLEMEYEN İZİNDE KUTU YOK — `Kapali.dc.html`
                  ================================================================
                  Tuval üç "kapalı" hâli yan yana çiziyor ve üçünü AYRI
                  çözüyor:

                    KİLİTLİ      anahtar var ama sabit + sebep
                    VERİLEMEYEN  **kutu YOK**, satır duruyor + sebep
                    MEŞGUL       içerik bilerek yok + sebep

                  Ayrım keyfî değil: kilitli ayar HERKES için sabittir,
                  yani anahtar "böyle bir ayar var ve hep açık" der.
                  Verilemeyen izin ise VAR ama SİZİN elinizde değil —
                  oradaki kutu, hiçbir zaman kullanamayacağınız bir
                  kola benziyor ve tıklatmaya davet ediyor.

                  Kutu YALNIZCA gerçekten kullanılamaz olduğunda
                  kalkıyor (`!verebilirMiyim`). Salt okuma modunda
                  duruyor: orada kutu bir eylem sunmuyor, rolün o izne
                  SAHİP OLUP OLMADIĞINI gösteriyor — tek bilgi taşıyıcı
                  o.

                  Bilgi kaybı da yok: izin verilmişse kapsam kutusu
                  (`Secim`) zaten çiziliyor, yani "bu rolde var" bilgisi
                  onun varlığından okunuyor.
                */
                const kutusuz = !salt && !p.verebilirMiyim;

                return (
                  <li
                    key={p.code}
                    className={cn(
                      "rounded-alan flex flex-wrap items-center gap-2 px-2 py-1",
                      kapali ? "bg-notr-50" : "hover:bg-notr-50",
                    )}
                  >
                    {kutusuz ? null : (
                      <input
                        type="checkbox"
                        id={`izin-${p.code}`}
                        checked={secilenKapsam !== null}
                        disabled={kapali}
                        onChange={(e) =>
                          degistir(
                            p.code,
                            e.target.checked
                              ? // Varsayilan: aktorun KENDI kapsami; yoksa
                                // katalogun ilk kapsami. Genisi zaten
                                // sunucu reddediyor.
                                (p.kendiKapsamim ?? p.allowedScopes[0] ?? "SELF")
                              : null,
                          )
                        }
                        className="accent-marka-600 size-4 shrink-0"
                      />
                    )}
                    {/*
                      Kutu yokken `label` da yok: `htmlFor` boşa
                      işaret eden bir etiket, ekran okuyucuda "etiket
                      var ama denetimi yok" diye okunur ve olmayan bir
                      denetim arattırır.
                    */}
                    {kutusuz ? (
                      <span
                        className={cn("min-w-0 flex-1 text-sm", "text-notr-600")}
                        // Kutu kalkinca satirin sol hizasi kayiyordu;
                        // bosluk kutu genisligi kadar korunuyor.
                        style={{ marginInlineStart: "1.5rem" }}
                      >
                        <span className="font-mono text-xs">{p.code}</span>
                        <span className="text-notr-600 ml-2 text-xs">{p.name}</span>
                      </span>
                    ) : (
                      <label
                        htmlFor={`izin-${p.code}`}
                        className={cn(
                          "min-w-0 flex-1 text-sm",
                          kapali ? "text-notr-600" : "text-notr-800",
                        )}
                      >
                        <span className="font-mono text-xs">{p.code}</span>
                        <span className="text-notr-600 ml-2 text-xs">{p.name}</span>
                      </label>
                    )}

                    {p.riskLevel >= 2 && (
                      <span
                        className={cn(
                          "rounded-alan shrink-0 px-2.5 py-0.5 text-xs font-medium",
                          RISK_SINIFI[p.riskLevel],
                        )}
                      >
                        {RISK_ADI[p.riskLevel]}
                      </span>
                    )}

                    {/* Iki sebep AYRI yaziliyor — bkz. yukaridaki not. */}
                    {!salt && p.isRestricted && (
                      <span className="text-notr-600 shrink-0 text-xs">
                        Kısıtlı — özel role eklenemez
                      </span>
                    )}
                    {!salt && !p.isRestricted && !p.verebilirMiyim && (
                      <span className="text-uyari-600 shrink-0 text-xs">
                        Sizde yok — veremezsiniz
                      </span>
                    )}

                    {secilenKapsam !== null && (
                      <Secim
                        etiket={`${p.code} kapsamı`}
                        etiketGizli
                        className="shrink-0"
                        value={secilenKapsam}
                        disabled={kapali}
                        onChange={(e) => degistir(p.code, e.target.value as Kapsam)}
                      >
                        {p.allowedScopes.map((k) => (
                          <option key={k} value={k}>
                            {KAPSAM_ADI[k]}
                          </option>
                        ))}
                      </Secim>
                    )}
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------

/**
 * YÜZEY SEÇENEKLERİ — F-ROL-04b
 *
 * Açıklamalar "panel" / "öğrenci uygulaması" demiyor yalnızca; KİMİN
 * için olduğunu söylüyor. Rol tanımlayan kişi teknik adı değil, o rolü
 * kimin taşıyacağını biliyor.
 */
const YUZEY_SECENEKLERI: ReadonlyArray<{
  deger: RolYuzeyi;
  baslik: string;
  aciklama: string;
}> = [
  {
    deger: "PANEL",
    baslik: "Yönetim paneli",
    aciklama: "Kurumda çalışan biri: uzman, koordinatör, satış, finans, yönetici.",
  },
  {
    deger: "STUDENT_APP",
    baslik: "Öğrenci ve veli uygulaması",
    aciklama: "Hizmet alan biri: öğrenci ya da veli. Panele giremez.",
  },
];

function YeniRol() {
  const istemci = useQueryClient();
  const [kod, setKod] = useState("");
  const [ad, setAd] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [kapsam, setKapsam] = useState<Kapsam>("SELF");
  /**
   * Rolün yüzeyi — F-ROL-04b.
   *
   * ================================================================
   * BAŞLANGIÇTA `null` — VE BU BİLEREK
   * ================================================================
   * `useState("PANEL")` yazsaydık seçenek ÖNCEDEN İŞARETLİ gelirdi ve
   * rolü tanımlayan kişi hiçbir şey seçmeden formu gönderebilirdi.
   * Alanın var olma sebebi tam olarak bu: sistem tahmin etmesin,
   * kişi SÖYLESİN. Ön seçim, tahmini ekrana taşımaktan başka bir şey
   * olmazdı.
   *
   * Seçilmeden "Rolü oluştur" düğmesi açılmıyor.
   */
  const [yuzey, setYuzey] = useState<RolYuzeyi | null>(null);
  const [secili, setSecili] = useState<Map<string, Kapsam>>(new Map());

  const katalog = useQuery({ queryKey: ["izin-katalogu"], queryFn: izinKatalogu });

  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Rol ve izin değişiklikleri geri alınması zor sonuçlar doğurur. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const mut = useMutation({
    mutationFn: () =>
      rolOlustur({
        kod: kod.trim(),
        ad: ad.trim(),
        aciklama: aciklama.trim() || null,
        varsayilanKapsam: kapsam,
        // `!` GÜVENLİ: düğme `yuzey === null` iken kapalı ve sunucu da
        // alanı zorunlu tutuyor (400).
        yuzey: yuzey!,
        izinler: [...secili].map(([code, scope]) => ({ code, scope })),
      }),
    onSuccess: async () => {
      setKod("");
      setAd("");
      setAciklama("");
      setYuzey(null);
      setSecili(new Map());
      await istemci.invalidateQueries({ queryKey: ["roller"] });
    },
    onError: hataYakala(() => mut.mutate()),
  });

  if (katalog.isPending) return <Iskelet satir={4} />;
  if (katalog.isError) {
    const kapiEkrani = sorguKapisi(katalog.error, () => void katalog.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
    return <BosDurum ton="hata" baslik="İzin kataloğu alınamadı" />;
  }

  const kodGecerli = /^[A-Z][A-Z0-9_]*$/.test(kod.trim());
  const eksik = !kodGecerli || ad.trim().length < 2 || yuzey === null;

  return (
    /*
      Sudo kapisi formun ICINE konulamaz: kendisi de bir <form> ve ic
      ice form gecersiz HTML — tarayici ictekini yok sayar, sifre
      alani da "Dogrula" dugmesi de calismaz gorunurdu.
    */
    <>
      {kapi}
      <form
        className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-4 border p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!eksik) mut.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Alan
            etiket="Rol kodu"
            value={kod}
            onChange={(e) => setKod(e.target.value.toUpperCase())}
            placeholder="ICERIK_EDITORU"
            hata={kod.length > 0 && !kodGecerli ? "BÜYÜK_HARF ve alt çizgi olmalı." : undefined}
          />
          <Alan etiket="Görünen ad" value={ad} onChange={(e) => setAd(e.target.value)} />
          <Alan etiket="Açıklama" value={aciklama} onChange={(e) => setAciklama(e.target.value)} />
          <Secim
            etiket="Varsayılan kapsam"
            value={kapsam}
            onChange={(e) => setKapsam(e.target.value as Kapsam)}
          >
            {(Object.keys(KAPSAM_ADI) as Kapsam[]).map((k) => (
              <option key={k} value={k}>
                {KAPSAM_ADI[k]}
              </option>
            ))}
          </Secim>
        </div>

        {/*
          ================================================================
          YÜZEY SEÇİMİ — İZİNLERDEN ÖNCE
          ================================================================
          İzin listesinin ALTINA koysaydık, iki yüz satırlık bir
          seçicinin arkasında kalırdı ve zaten en kolay atlanacak alan
          o olurdu.

          `Secim` (açılır liste) DEĞİL: iki seçenek var ve ikisinin de
          bir SONUCU var — hangi uygulamanın açılacağı. Açılır listede
          seçenekler kapalı durur ve yanlarına açıklama yazılamaz;
          burada asıl iş o açıklamalar.
        */}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-notr-900 mb-1 text-sm font-medium">
            Bu rolü taşıyan kişi hangi uygulamayı görür?
          </legend>
          <p className="text-notr-600 -mt-1 mb-1 text-xs">
            Sistem bunu tahmin etmiyor. Yanlış seçim, kişiyi kullanamayacağı bir uygulamaya düşürür.
          </p>
          {YUZEY_SECENEKLERI.map((y) => (
            <label
              key={y.deger}
              className={cn(
                "rounded-alan flex cursor-pointer gap-3 border p-3",
                yuzey === y.deger ? "border-marka-600 bg-marka-50" : "border-notr-200 bg-notr-0",
              )}
            >
              <input
                type="radio"
                name="rol-yuzeyi"
                checked={yuzey === y.deger}
                onChange={() => setYuzey(y.deger)}
                className="mt-1"
              />
              <span className="min-w-0">
                <span className="text-notr-900 block text-sm font-medium">{y.baslik}</span>
                <span className="text-notr-600 block text-xs">{y.aciklama}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <IzinSecici
          katalog={katalog.data}
          secili={secili}
          salt={false}
          onDegis={(m) => setSecili(new Map(m))}
        />

        <div>
          <Buton type="submit" disabled={eksik || mut.isPending}>
            {mut.isPending ? "Oluşturuluyor…" : "Rolü oluştur"}
          </Buton>
        </div>

        {mut.isError && (
          <p role="alert" className="text-hata-500 text-sm">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Rol oluşturulamadı."}
          </p>
        )}
        {mut.isSuccess && (
          <p role="status" className="text-onay-700 text-sm">
            "{mut.data.name}" rolü oluşturuldu.
          </p>
        )}
      </form>
    </>
  );
}

// ---------------------------------------------------------------

/** Gerekce en az bu kadar — sunucudaki kuralin aynisi. */
const EN_AZ_GEREKCE = 10;

function KullaniciIzni() {
  const [sorgulanan, setSorgulanan] = useState<string | null>(null);
  const [kod, setKod] = useState("");
  const [etki, setEtki] = useState<"GRANT" | "DENY">("GRANT");
  const [gerekce, setGerekce] = useState("");

  const istemci = useQueryClient();
  const katalog = useQuery({ queryKey: ["izin-katalogu"], queryFn: izinKatalogu });

  /**
   * ================================================================
   * KIMLIK YAPISTIRMA YERINE KISI SECIMI
   * ================================================================
   * Burada "Kullanici kimligi" yazan bir metin kutusu vardi ve
   * `cms5q65g0009v730...` gibi bir kimlik bekliyordu. O kimligi
   * kimse bilmiyor, panelde gorulebilecegi bir yer de yok: ekran
   * calisiyor gorunuyordu ama kullanilamiyordu.
   */
  const personel = useQuery({
    queryKey: ["personel", false],
    queryFn: () => personelListesi(false),
    retry: false,
  });

  const liste = useQuery({
    queryKey: ["kullanici-izinleri", sorgulanan],
    queryFn: () => kullaniciIzinleri(sorgulanan!),
    enabled: sorgulanan !== null,
  });

  const { hataYakala, kapi } = useSudoKapisi(
    "Rol ve izin değişiklikleri geri alınması zor sonuçlar doğurur. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const mut = useMutation({
    mutationFn: () => kullaniciIzniVer({ userId: sorgulanan!, kod, etki, gerekce: gerekce.trim() }),
    onSuccess: async () => {
      setGerekce("");
      await istemci.invalidateQueries({ queryKey: ["kullanici-izinleri", sorgulanan] });
    },
    onError: hataYakala(() => mut.mutate()),
  });

  const kisa = gerekce.trim().length < EN_AZ_GEREKCE;

  return (
    <div className="flex flex-col gap-4">
      {kapi}
      {personel.isPending ? (
        <Iskelet satir={2} />
      ) : personel.isError ? (
        <p className="text-notr-600 text-sm">Personel listesi alinamadi; kisi secilemiyor.</p>
      ) : (
        <Secim
          etiket="Kisi"
          value={sorgulanan ?? ""}
          onChange={(e) => setSorgulanan(e.target.value || null)}
        >
          <option value="">Seciniz</option>
          {personel.data.personel.map((p) => (
            <option key={p.id} value={p.id}>
              {p.firstName} {p.lastName} ({p.roller.join(", ") || "rolsuz"})
            </option>
          ))}
        </Secim>
      )}

      {sorgulanan && <RolAtama userId={sorgulanan} />}

      {sorgulanan && (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="text-notr-900 text-sm font-semibold">Etkin istisnalar</h3>
            {liste.isPending && <Iskelet satir={4} />}
            {liste.isError && (
              <p role="alert" className="text-hata-500 text-sm">
                {liste.error instanceof ApiHatasi ? liste.error.message : "Alınamadı."}
              </p>
            )}
            {liste.data && liste.data.length === 0 && (
              <p className="text-notr-600 text-sm">Bu kullanıcıda izin istisnası yok.</p>
            )}
            {liste.data && liste.data.length > 0 && (
              <ul className="flex flex-col gap-2">
                {liste.data.map((k) => (
                  <Kart key={k.id} olarak="li" className="p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs">{k.code}</span>
                      <span
                        className={cn(
                          "rounded-alan px-2.5 py-0.5 text-xs font-medium",
                          k.effect === "GRANT"
                            ? "bg-basarili-500/15 text-onay-700"
                            : "bg-hata-500/15 text-hata-700",
                        )}
                      >
                        {k.effect === "GRANT" ? "Verildi" : "Kaldırıldı"}
                      </span>
                      {k.scope && (
                        <span className="text-notr-600 text-xs">{KAPSAM_ADI[k.scope]}</span>
                      )}
                    </div>
                    {/*
                      GEREKCE her zaman gosteriliyor: "neden verildi"
                      sorusunun cevabi kayitta duruyorsa ekranda da
                      durmali, yoksa kimse okumaz.
                    */}
                    <p className="text-notr-600 mt-1 text-xs">{k.reason}</p>
                  </Kart>
                ))}
              </ul>
            )}
          </section>

          <form
            className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!kisa && kod) mut.mutate();
            }}
          >
            <h3 className="text-notr-900 text-sm font-semibold">Yeni istisna</h3>

            <Secim etiket="İzin" value={kod} onChange={(e) => setKod(e.target.value)}>
              <option value="">Seçin…</option>
              {(katalog.data ?? []).map((p) => (
                <option key={p.code} value={p.code}>
                  {p.code} — {p.name}
                </option>
              ))}
            </Secim>

            <Secim
              etiket="Etki"
              value={etki}
              onChange={(e) => setEtki(e.target.value as "GRANT" | "DENY")}
            >
              <option value="GRANT">Ver</option>
              <option value="DENY">Kaldır</option>
            </Secim>

            <div className="flex flex-col gap-1.5">
              <MetinAlani
                etiket="Gerekçe"
                value={gerekce}
                onChange={(e) => setGerekce(e.target.value)}
                rows={3}
              />
              {/*
                Gerekce ZORUNLU ve sayac yazarken gorunuyor: hatayi
                gonderdikten sonra gormek, yoneticiyi yazdigini silip
                bastan yazmaya iter.
              */}
              <span
                id="gerekce-ipucu"
                className={cn("text-xs", kisa ? "text-uyari-600" : "text-notr-600")}
              >
                {kisa
                  ? `En az ${EN_AZ_GEREKCE} karakter — ${EN_AZ_GEREKCE - gerekce.trim().length} tane daha`
                  : "Bu metin denetim kaydına yazılır."}
              </span>
            </div>

            <div>
              <Buton type="submit" disabled={kisa || !kod || mut.isPending}>
                {mut.isPending ? "Kaydediliyor…" : "İstisnayı kaydet"}
              </Buton>
            </div>

            {mut.isError && (
              <p role="alert" className="text-hata-500 text-sm">
                {mut.error instanceof ApiHatasi ? mut.error.message : "Kaydedilemedi."}
              </p>
            )}
          </form>
        </>
      )}
    </div>
  );
}

/**
 * ROL ATAMA — F-ROL-15a, F-ROL-17
 *
 * `POST /authz/rol-ata` yazılıydı, hiçbir ekran çağırmıyordu. Personel
 * ekranındaki rol değiştirme uçtan farklı: orası kişinin rol
 * listesinin TAMAMINI değiştiriyor, burası TEK bir rolü, isterse
 * daraltılmış bir kapsamla ekliyor.
 *
 * ================================================================
 * KAPSAM DARALTMAK İÇİN — GENİŞLETMEK İÇİN DEĞİL
 * ================================================================
 * Rolün varsayılan kapsamı `defaultScope`; burada verilen kapsam onun
 * yerine geçiyor. Sunucu kendi kapsamından geniş vermeyi reddediyor
 * (`assertCanGrant`), yani buradan yetki genişletilemez.
 *
 * Boş bırakılırsa rolün kendi varsayılanı kullanılıyor — ekran
 * "varsayılan" diye ayrı bir seçenek gösteriyor, çünkü `ALL` seçmekle
 * "dokunma" arasındaki fark kullanıcıya görünmezdi.
 */
function RolAtama({ userId }: { userId: string }) {
  const istemci = useQueryClient();
  const [rolId, setRolId] = useState("");
  const [kapsam, setKapsam] = useState<Kapsam | "">("");
  const [bitti, setBitti] = useState<string | null>(null);

  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Rol atamak kişinin görebildiği veriyi değiştirir; kimliğinizi doğrulayın.",
  );

  const roller = useQuery({ queryKey: ["roller"], queryFn: rolleriGetir, retry: false });

  const mut = useMutation({
    mutationFn: () => rolAta({ userId, rolId, kapsam: kapsam === "" ? null : kapsam }),
    onSuccess: async (c) => {
      setBitti(c.rolKodu);
      await istemci.invalidateQueries({ queryKey: ["personel"] });
      await istemci.invalidateQueries({ queryKey: ["roller"] });
    },
    onError: hataYakala(() => mut.mutate()),
  });

  if (roller.isPending) return <Iskelet satir={2} />;

  if (roller.isError) {
    const kapiEkrani = sorguKapisi(roller.error, () => void roller.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
    return <p className="text-notr-600 text-sm">Rol listesi alınamadı; rol atanamıyor.</p>;
  }

  const secili = roller.data.find((r) => r.id === rolId);

  return (
    <section className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-3 border p-4">
      {kapi}
      <h3 className="text-notr-900 text-sm font-semibold">Rol ata</h3>
      <p className="text-notr-600 text-xs">
        Kişiye tek bir rol ekler. Rol listesinin tamamını değiştirmek için Personel ekranını
        kullanın. Rol değişiminde kişinin tüm oturumları kapanır{/* G-SES-05 */}.
      </p>

      {bitti !== null && (
        <p className="rounded-alan bg-onay-50 text-notr-800 px-3 py-2 text-sm">
          <strong>{bitti}</strong> rolü atandı.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Secim
          etiket="Rol"
          value={rolId}
          onChange={(e) => {
            setRolId(e.target.value);
            setBitti(null);
          }}
        >
          <option value="">Seçiniz</option>
          {roller.data
            .filter((r) => r.isActive)
            .map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
        </Secim>

        <Secim
          etiket="Kapsam"
          value={kapsam}
          onChange={(e) => setKapsam(e.target.value as Kapsam | "")}
        >
          <option value="">
            Rolün varsayılanı{secili ? ` (${KAPSAM_ADI[secili.defaultScope]})` : ""}
          </option>
          {(Object.keys(KAPSAM_ADI) as Kapsam[])
            .filter((k) => k !== "NONE")
            .map((k) => (
              <option key={k} value={k}>
                {KAPSAM_ADI[k]}
              </option>
            ))}
        </Secim>
      </div>

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Rol atanamadı."}
        </p>
      )}

      <div>
        <Buton yukleniyor={mut.isPending} disabled={rolId === ""} onClick={() => mut.mutate()}>
          Rolü ata
        </Buton>
      </div>
    </section>
  );
}
